import React, { useEffect, useState, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import FinancialAccountService from "../../services/FinancialAccountService";
import api from "../../api/axios";
import { useSearchParams } from "react-router-dom";

const accountStyles = {
  1: { icon: "pi pi-money-bill", label: "Kasa" },
  2: { icon: "pi pi-building", label: "Banka" },
  3: { icon: "pi pi-credit-card", label: "Firma Kartı" },
  4: { icon: "pi pi-print", label: "POS Cihazı" },
  default: { icon: "pi pi-wallet", label: "Hesap" }
};

export default function CreditCardInstallmentsPage() {
  const [workTrackingParams, setWorkTrackingParams] = useSearchParams();
  const [highlightedTransactionId] = useState(() => Number(workTrackingParams.get("highlight")));
  const highlightedPlanId = Number(workTrackingParams.get("installmentId"));
  const workTrackingActionHandled = useRef(false);
  const [installments, setInstallments] = useState([]);
  const [expandedRows, setExpandedRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [payDialogVisible, setPayDialogVisible] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [sourceAccountId, setSourceAccountId] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [accountFilter, setAccountFilter] = useState(null);
  const [dateFilter, setDateFilter] = useState("CURRENT_MONTH");
  const toast = useRef(null);

  const accountValueTemplate = (option, props) => {
    if (!option) return props.placeholder;
    const style = accountStyles[option.accountType] || accountStyles.default;
    return (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <i className={`${style.icon} text-slate-500`} />
          <span className="font-bold text-slate-700 text-xs">{option.name}</span>
        </div>
        <span className="text-[11px] font-black text-slate-500 mr-2">
          Bakiyeniz: {(option.balance ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {option.currencyCode || "TRY"}
        </span>
      </div>
    );
  };

  const accountOptionTemplate = (option) => {
    if (!option) return null;
    const style = accountStyles[option.accountType] || accountStyles.default;
    return (
      <div className="flex items-center justify-between w-full py-1">
        <div className="flex items-center gap-3 min-w-0">
          <i className={`${style.icon} text-slate-400 text-base flex-shrink-0`} />
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-slate-700 text-xs truncate">{option.name}</span>
            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">{style.label}</span>
          </div>
        </div>
        <span className="font-extrabold text-slate-600 text-xs ml-4 flex-shrink-0">
          {(option.balance ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {option.currencyCode || "TRY"}
        </span>
      </div>
    );
  };

  useEffect(() => {
    loadInstallments();
    loadAccounts();
  }, []);

  useEffect(() => {
    if (workTrackingActionHandled.current || installments.length === 0) {
      return;
    }

    const hasTransactionHighlight = Number.isFinite(highlightedTransactionId) && highlightedTransactionId > 0;
    const hasPlanHighlight = Number.isFinite(highlightedPlanId) && highlightedPlanId > 0;
    if (!hasTransactionHighlight && !hasPlanHighlight) return;

    const plan = installments.find((item) => (
      (hasPlanHighlight && Number(item.id) === highlightedPlanId) ||
      (item.installments || []).some((installment) => Number(installment.transactionId) === highlightedTransactionId)
    ));
    if (plan) {
      const installment = (plan.installments || []).find((item) => Number(item.transactionId) === highlightedTransactionId);
      setExpandedRows({ [plan.id]: true });
      if (installment && !installment.isPaid) {
        setSelectedInstallment(installment);
        setSourceAccountId(null);
        setPayDialogVisible(true);
      }

      workTrackingActionHandled.current = true;
      const nextParams = new URLSearchParams(workTrackingParams);
      nextParams.delete("highlight");
      nextParams.delete("installmentId");
      setWorkTrackingParams(nextParams, { replace: true });
    }
  }, [highlightedPlanId, highlightedTransactionId, installments, setWorkTrackingParams, workTrackingParams]);

  const loadInstallments = async () => {
    try {
      setLoading(true);
      const res = await FinancialAccountService.getInstallments();
      if (res.success) {
        setInstallments(res.data || []);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: res.message || "Taksit planları yüklenemedi!"
        });
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Taksit planları yüklenirken bir hata oluştu."
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const res = await FinancialAccountService.getAll();
      if (res.success) {
        setAccounts(res.data || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const openPayDialog = (installment) => {
    setSelectedInstallment(installment);
    setSourceAccountId(null);
    setPayDialogVisible(true);
  };

  const handlePayInstallment = async () => {
    if (!sourceAccountId) {
      toast.current.show({ severity: "warn", summary: "Uyarı", detail: "Lütfen ödeme yapılacak kaynak hesabı seçin!" });
      return;
    }
    try {
      setPayLoading(true);
      const res = await api.post("/FinancialAccount/pay-installment", {
        transactionId: selectedInstallment.transactionId,
        sourceAccountId: sourceAccountId
      });
      const result = res.data || res;
      if (result.success) {
        toast.current.show({ severity: "success", summary: "Başarılı", detail: "Taksit başarıyla ödendi." });
        setPayDialogVisible(false);
        loadInstallments();
      } else {
        toast.current.show({ severity: "error", summary: "Hata", detail: result.message || "Taksit ödemesi başarısız." });
      }
    } catch (e) {
      toast.current.show({ severity: "error", summary: "Hata", detail: "Taksit ödenirken hata oluştu." });
    } finally {
      setPayLoading(false);
    }
  };

  // Metrics
  const totalInstallmentDebt = installments.reduce((sum, item) => sum + item.remainingAmount, 0);
  const totalPaidAmount = installments.reduce((sum, item) => sum + item.paidAmount, 0);
  const activePlansCount = installments.filter(item => item.remainingAmount > 0).length;

  const cardAccountOptions = accounts
    .filter((a) => a.accountType === 3)
    .map((a) => ({ label: a.name, value: a.id }));

  const clearFilters = () => {
    setGlobalFilter("");
    setStatusFilter("ALL");
    setAccountFilter(null);
    setDateFilter("CURRENT_MONTH");
  };

  const filteredInstallments = installments.filter((item) => {
    if (statusFilter === "ACTIVE" && item.remainingAmount <= 0) return false;
    if (statusFilter === "COMPLETED" && item.remainingAmount > 0) return false;
    if (accountFilter && Number(item.financialAccountId) !== Number(accountFilter)) return false;
    if (dateFilter === "CURRENT_MONTH") {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const hasCurrentMonthInstallment = (item.installments || []).some((inst) => {
        const d = new Date(inst.dueDate);
        return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
      });
      if (!hasCurrentMonthInstallment && item.remainingAmount <= 0) return false;
    }
    if (globalFilter.trim()) {
      const q = globalFilter.toLowerCase();
      const matchName = String(item.financialAccountName || "").toLowerCase().includes(q);
      const matchRef = String(item.referenceName || "").toLowerCase().includes(q);
      const matchDesc = String(item.description || "").toLowerCase().includes(q);
      if (!matchName && !matchRef && !matchDesc) return false;
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
  };

  const statusBodyTemplate = (rowData) => {
    const isCompleted = rowData.remainingAmount <= 0;
    return (
      <Tag
        value={isCompleted ? "TAMAMLANDI" : "AKTİF"}
        severity={isCompleted ? "success" : "warning"}
        className="text-[9px] font-black tracking-widest px-3 py-1.5 rounded-full"
      />
    );
  };

  const progressBodyTemplate = (rowData) => {
    const total = rowData.installmentCount;
    const paid = total - rowData.remainingInstallmentCount;
    const value = Math.round((paid / total) * 100);

    return (
      <div className="w-full max-w-[120px]">
        <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 mb-1">
          <span>{paid} / {total} Taksit</span>
          <span>%{value}</span>
        </div>
        <ProgressBar value={value} showValue={false} className="h-1.5 bg-slate-100 rounded-full" style={{ height: '6px' }} />
      </div>
    );
  };

  const amountBodyTemplate = (rowData) => {
    return <span className="font-extrabold text-slate-800">{rowData.totalAmount.toLocaleString()} ₺</span>;
  };

  const paidAmountBodyTemplate = (rowData) => {
    return <span className="font-extrabold text-emerald-600">+{rowData.paidAmount.toLocaleString()} ₺</span>;
  };

  const remainingAmountBodyTemplate = (rowData) => {
    return <span className="font-extrabold text-rose-600">{rowData.remainingAmount.toLocaleString()} ₺</span>;
  };

  const typeBodyTemplate = (rowData) => {
    const isExpense = rowData.referenceType === "EXPENSE";
    return (
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${isExpense ? 'bg-rose-500' : 'bg-emerald-500'}`}></span>
        <div>
          <span className="text-[10px] font-black text-slate-400 block tracking-wider leading-none mb-1">{isExpense ? 'GİDER' : 'ÖDEME'}</span>
          <span className="text-xs font-bold text-slate-700 leading-tight">{rowData.referenceName}</span>
        </div>
      </div>
    );
  };

  const accountBodyTemplate = (rowData) => {
    return (
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-xl bg-slate-900 text-white flex items-center justify-center shadow-md">
          <i className="pi pi-credit-card text-xs"></i>
        </div>
        <div>
          <span className="text-xs font-black text-slate-800 block leading-tight">{rowData.financialAccountName}</span>
          <span className="text-[10px] font-bold text-slate-400 block leading-none mt-0.5">{formatDate(rowData.firstInstallmentDate)} Başlangıç</span>
        </div>
      </div>
    );
  };

  const rowExpansionTemplate = (data) => {
    return (
      <div className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
        <h5 className="text-xs font-black text-slate-500 tracking-wider uppercase mb-3">Ödeme Takvimi Detayları</h5>
        <DataTable
          value={data.installments}
          dataKey="transactionId"
          responsiveLayout="scroll"
          className="p-datatable-sm modern-table"
          rowClassName={(row) => Number(row.transactionId) === highlightedTransactionId ? "!bg-amber-50 ring-1 ring-inset ring-amber-300" : ""}
        >
          <Column
            header="#"
            body={(rowData, options) => <span className="font-bold text-slate-400">{options.rowIndex + 1}</span>}
            style={{ width: '4rem' }}
          />
          <Column
            field="dueDate"
            header="Vade Tarihi"
            body={(rowData) => <span className="font-bold text-slate-700">{formatDate(rowData.dueDate)}</span>}
          />
          <Column
            field="amount"
            header="Tutar"
            body={(rowData) => <span className="font-extrabold text-slate-800">{rowData.amount.toLocaleString()} ₺</span>}
          />
          <Column
            field="description"
            header="Açıklama"
            body={(rowData) => <span className="text-xs font-semibold text-slate-500">{rowData.description}</span>}
          />
          <Column
            field="isPaid"
            header="Durum / İşlem"
            body={(rowData) => (
              <div className="flex items-center gap-3">
                <Tag
                  value={rowData.isPaid ? "ÖDENDİ" : "BEKLENİYOR"}
                  severity={rowData.isPaid ? "success" : "info"}
                  className="text-[9px] font-black tracking-widest px-2.5 py-1.5 rounded-full"
                />
                {!rowData.isPaid && (
                  <Button
                    icon="pi pi-check"
                    className="p-button-rounded p-button-success p-button-sm p-button-outlined"
                    tooltip="Bu taksiti kasa/bankadan el ile öde"
                    onClick={() => openPayDialog(rowData)}
                    style={{ width: '30px', height: '30px' }}
                  />
                )}
              </div>
            )}
          />
        </DataTable>
      </div>
    );
  };

  const payDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="İptal" className="p-button-text p-button-secondary rounded-xl" onClick={() => setPayDialogVisible(false)} />
      <Button label="Ödemeyi Kaydet" icon="pi pi-check" className="p-button-success rounded-xl" loading={payLoading} onClick={handlePayInstallment} />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <Toast ref={toast} />

      {/* Header with Title and Quick Refresh */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-2">Taksit Ödeme Planlarım</h1>
          <p className="text-xs font-semibold text-slate-400">Şirket kredi kartları ile yapılan taksitli işlemlerin ve vadeli ödemelerin takibi</p>
        </div>
        <Button
          icon="pi pi-refresh"
          onClick={loadInstallments}
          loading={loading}
          className="p-button-outlined p-button-secondary rounded-2xl px-4 py-2 text-xs font-bold"
          label="Yenile"
        />
      </div>

      {/* Modern Dashboard Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-rose-100 dark:border-rose-950/20 bg-gradient-to-br from-white to-rose-50/20 dark:from-slate-900 rounded-3xl shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-black text-slate-400 tracking-widest uppercase">KALAN TOPLAM BORÇ</span>
            <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center shadow-sm">
              <i className="pi pi-minus-circle"></i>
            </div>
          </div>
          <div className="text-2xl font-black text-rose-600">{totalInstallmentDebt.toLocaleString()} ₺</div>
          <p className="text-[10px] text-slate-400 font-bold mt-2">Kartlardan çekilecek toplam taksit borcu</p>
        </Card>

        <Card className="border border-emerald-100 dark:border-emerald-950/20 bg-gradient-to-br from-white to-emerald-50/20 dark:from-slate-900 rounded-3xl shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-black text-slate-400 tracking-widest uppercase">ÖDENEN TOPLAM</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center shadow-sm">
              <i className="pi pi-check-circle"></i>
            </div>
          </div>
          <div className="text-2xl font-black text-emerald-600">{totalPaidAmount.toLocaleString()} ₺</div>
          <p className="text-[10px] text-slate-400 font-bold mt-2">Bugüne kadar kesilmiş/ödenmiş taksitler</p>
        </Card>

        <Card className="border border-sky-100 dark:border-sky-950/20 bg-gradient-to-br from-white to-sky-50/20 dark:from-slate-900 rounded-3xl shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-black text-slate-400 tracking-widest uppercase">AKTİF PLAN SAYISI</span>
            <div className="w-8 h-8 rounded-xl bg-sky-50 text-sky-500 flex items-center justify-center shadow-sm">
              <i className="pi pi-calendar-plus"></i>
            </div>
          </div>
          <div className="text-2xl font-black text-sky-600">{activePlansCount} Plan</div>
          <p className="text-[10px] text-slate-400 font-bold mt-2">Gelecek aylarda ödemesi devam edecek planlar</p>
        </Card>
      </div>

      {/* Filter Bar */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 bg-white dark:bg-slate-900 p-4 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm items-end">
        <div className="md:col-span-4 flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Arama</label>
          <span className="p-input-icon-left w-full">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="İşlem, kart veya açıklama ara..."
              className="w-full rounded-2xl border-slate-200 py-2.5 text-xs font-bold"
            />
          </span>
        </div>
        <div className="md:col-span-3 flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Plan Durumu</label>
          <Dropdown
            value={statusFilter}
            options={[
              { label: "Tüm Planlar", value: "ALL" },
              { label: "Aktif Ödemesi Sürenler", value: "ACTIVE" },
              { label: "Tamamlananlar", value: "COMPLETED" },
            ]}
            onChange={(e) => setStatusFilter(e.value)}
            placeholder="Plan durumu seçin"
            className="w-full rounded-2xl border-slate-200 font-bold text-sm h-[42px] flex items-center"
          />
        </div>
        <div className="md:col-span-3 flex flex-col gap-1.5">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">Kart / Hesap</label>
          <Dropdown
            value={accountFilter}
            options={[
              { label: "Tüm Kartlar / Hesaplar", value: null },
              ...cardAccountOptions,
            ]}
            onChange={(e) => setAccountFilter(e.value)}
            placeholder="Kart seçin"
            className="w-full rounded-2xl border-slate-200 font-bold text-sm h-[42px] flex items-center"
          />
        </div>
        <div className="md:col-span-2 flex gap-2">
          <Button
            label="Temizle"
            icon="pi pi-filter-slash"
            outlined
            severity="secondary"
            className="w-full !rounded-2xl font-black text-xs py-3"
            onClick={clearFilters}
          />
        </div>
      </div>

      {/* Main Installments Data Table */}
      <Card className="border border-slate-100 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 backdrop-blur-md rounded-3xl shadow-sm overflow-hidden p-0">
        <DataTable
          value={filteredInstallments}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          dataKey="id"
          loading={loading}
          responsiveLayout="scroll"
          className="modern-table text-slate-700"
          rowClassName={(row) => {
            const containsHighlighted = (row.installments || []).some((item) => Number(item.transactionId) === highlightedTransactionId);
            return `border-b border-slate-100 ${containsHighlighted ? "!bg-amber-50" : ""}`;
          }}
          emptyMessage="Taksitli işlem bulunamadı."
        >
          <Column expander style={{ width: '3em' }} />
          <Column header="Kart / Hesap" body={accountBodyTemplate} style={{ minWidth: '14rem' }} />
          <Column header="İlişkili İşlem / Cari" body={typeBodyTemplate} style={{ minWidth: '12rem' }} />
          <Column header="İlerleme" body={progressBodyTemplate} style={{ minWidth: '10rem' }} />
          <Column header="Açıklama" field="description" body={(rowData) => <span className="text-xs font-semibold text-slate-500">{rowData.description || "-"}</span>} />
          <Column header="Toplam Tutar" body={amountBodyTemplate} />
          <Column header="Ödenen" body={paidAmountBodyTemplate} />
          <Column header="Kalan" body={remainingAmountBodyTemplate} />
          <Column header="Durum" body={statusBodyTemplate} style={{ width: '8rem', textAlign: 'center' }} />
        </DataTable>
      </Card>

      {/* Pay Installment Dialog */}
      <Dialog
        visible={payDialogVisible}
        onHide={() => setPayDialogVisible(false)}
        header="Taksit Ödemesi Yap"
        footer={payDialogFooter}
        className="w-full max-w-md rounded-3xl"
        contentStyle={{ minHeight: "260px", overflow: "visible" }}
      >
        <div className="space-y-4 py-2">
          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">TAKSİT TUTARI</span>
            <div className="text-xl font-black text-slate-800">{selectedInstallment?.amount.toLocaleString()} ₺</div>
            <p className="text-[10px] text-slate-400 font-bold mt-1">{selectedInstallment?.description}</p>
          </div>
          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider ml-1">ÖDEMENİN YAPILACAĞI HESAP</label>
            <Dropdown
              value={sourceAccountId}
              options={accounts.filter(a => a.accountType !== 3)} // Don't pay credit card with another credit card
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setSourceAccountId(e.value)}
              placeholder="Ödeme kaynağı hesap seçin..."
              className="w-full rounded-2xl border-slate-200 font-bold"
              itemTemplate={accountOptionTemplate}
              valueTemplate={accountValueTemplate}
              appendTo="self"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
